"use client"; 

import { useState, useRef, useCallback, useEffect } from 'react'
import type React from 'react'
import { cn } from '@/lib/utils'
import { MessageCircle, Minus, X, Minimize2, Maximize2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    AlertDialog,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from '@/components/ui/alert-dialog'

type Position = {
    x: number
    y: number
}

type Size = {
    width: number
    height: number
}

type Props = {
    children: React.ReactNode
    className?: string
    minSize?: Size
    isOpen?: boolean
    onClose?: () => void
    onCreateNewSession?: () => void
    // Texts for the close confirmation dialog
    title?: string
    // Optional icon element to show next to the title
    // Accepts any ReactNode: e.g. <MessageCircle className="..." /> or a custom SVG element
    titleIcon?: React.ReactNode
    closeDialogTitle?: string
    closeDialogDescription?: string
    closeDialogCancelLabel?: string
    closeDialogActionLabel?: string
    // Labels for header buttons
    closeButtonLabel?: string
    minimizeButtonLabel?: string
    maximizeButtonLabel?: string
    restoreButtonLabel?: string
}

export const Widget: React.FC<Props> = ({
    children,
    className,
    minSize = { width: 400, height: 600 },
    isOpen = false,
    onClose,
    onCreateNewSession,
    title = "Chiki.Studio | Aura",
    titleIcon = <MessageCircle className="h-5 w-5 text-primary" />,
    closeDialogTitle = 'Close chat?',
    closeDialogDescription = 'This chat session will be ended and a new one will be created.',
    closeDialogCancelLabel = 'Cancel',
    closeDialogActionLabel = 'Close',
    closeButtonLabel = 'Close',
    minimizeButtonLabel = 'Minimize',
    maximizeButtonLabel = 'Maximize',
    restoreButtonLabel = 'Restore'
}) => {
    // Track viewport to handle window resizes gracefully
    const [viewport, setViewport] = useState<{ width: number; height: number }>({
        width: 0,
        height: 0,
    })

    useEffect(() => {
        const onResize = () => {
            setViewport({ width: window.innerWidth, height: window.innerHeight })
        }
        window.addEventListener('resize', onResize)
        onResize() // Initial size
        return () => window.removeEventListener('resize', onResize)
    }, [])

    const isMobile = viewport.width > 0 && viewport.width < 640
    const defaultSize = isMobile
        ? { width: viewport.width, height: viewport.height }
        : minSize
    const defaultPosition = isMobile
        ? { x: 0, y: 0 }
        : (() => {
            const MARGIN = 16
            return {
                x: Math.max(0, viewport.width - defaultSize.width - MARGIN),
                y: Math.max(0, viewport.height - defaultSize.height - MARGIN),
            }
        })()
    const readStored = <T,>(key: string, fallback: T): T => {
        try {
            const raw = localStorage.getItem(key)
            if (!raw) return fallback
            return JSON.parse(raw) as T
        } catch {
            return fallback
        }
    }

    const [position, setPosition] = useState<Position>(() => readStored<Position>('chat:widget:position', defaultPosition))
    const initialSize = readStored<Size>('chat:widget:size', defaultSize)
    const [size, setSize] = useState<Size>(() => initialSize)
    const [isMaximized, setIsMaximized] = useState(() => initialSize.width > minSize.width || initialSize.height > minSize.height)
    const [isDragging, setIsDragging] = useState(false)
    const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 })

    const widgetRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return

        const target = e.target as HTMLElement
        const header = headerRef.current
        if (!header) return

        const inHeader = target === header || header.contains(target)
        if (!inHeader) return

        const interactive = target.closest('button, [role="button"], a, input, textarea, select, [contenteditable="true"]')
        if (interactive) return

        setIsDragging(true)
        e.preventDefault()
        const rect = widgetRef.current?.getBoundingClientRect()
        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            })
        }
    }, [])

    const toggleMaximize = useCallback(() => {
        if (isDragging) setIsDragging(false)
        if (isMaximized) {
            setPosition(defaultPosition)
            setSize(minSize)
        } else {
            const newWidth = viewport.width / 2
            const newHeight = viewport.height / 1.5
            setPosition({ x: (viewport.width - newWidth) / 2, y: (viewport.height - newHeight) / 2 })
            setSize({ width: newWidth, height: newHeight })
        }
        setIsMaximized(!isMaximized)
    }, [isMaximized, isDragging, viewport.width, viewport.height, defaultPosition, minSize])

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) {
            const newX = Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x))
            const newY = Math.max(0, Math.min(window.innerHeight - size.height, e.clientY - dragOffset.y))

            setPosition({ x: newX, y: newY })
        }
    }, [isDragging, size, dragOffset])

    const handleMouseUp = useCallback(() => {
        setIsDragging(false)
    }, [])

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.body.style.cursor = isDragging ? 'grabbing' : 'default'
            document.body.style.userSelect = 'none'

            return () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
                document.body.style.cursor = 'default'
                document.body.style.userSelect = 'auto'
            }
        }
    }, [isDragging, handleMouseMove, handleMouseUp])

    useEffect(() => {
        setSize((s) => {
            const next = {
                width: Math.min(Math.max(s.width, minSize.width), viewport.width),
                height: Math.min(Math.max(s.height, minSize.height), viewport.height),
            }
            setPosition((p) => ({
                x: Math.max(0, Math.min(viewport.width - next.width, p.x)),
                y: Math.max(0, Math.min(Math.max(0, viewport.height - next.height), p.y)),
            }))
            return next
        })
    }, [viewport.width, viewport.height])

    useEffect(() => {
        localStorage.setItem('chat:widget:position', JSON.stringify(position))
    }, [position])

    useEffect(() => {
        localStorage.setItem('chat:widget:size', JSON.stringify(size))
    }, [size])

    return (
        <div
            ref={widgetRef}
            className={cn(
                "fixed bg-blue-50 dark:bg-gray-900 border-[2px] border-blue-200 dark:border-gray-700 rounded-lg z-50 overflow-hidden chat-widget",
                "flex flex-col",
                "transition-all duration-200 ease-in-out",
                isDragging && "dragging shadow-3xl",
                !isOpen && "hidden",
                className
            )}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${size.width}px`,
                height: `${size.height}px`,
            }}
        >
            <div
                ref={headerRef}
                className={cn(
                    "flex items-center justify-between p-4 bg-white dark:bg-gray-800 border-b-[2px] border-blue-200 dark:border-gray-700",
                    "cursor-grab active:cursor-grabbing",
                    isDragging && "cursor-grabbing"
                )}
                onMouseDown={handleMouseDown}
                onDoubleClick={toggleMaximize}
            >
                <div className="flex items-center gap-2">
                    {titleIcon}
                    <span className="text-lg font-medium select-none">
                        {title}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="h-6 w-6 hover:bg-blue-200 dark:hover:bg-gray-700"
                            aria-label={minimizeButtonLabel}
                            title={minimizeButtonLabel}
                        >
                            <Minus className="h-3 w-3" />
                        </Button>
                    )}
                    {!isMobile && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleMaximize}
                            className="h-6 w-6 hover:bg-blue-200 dark:hover:bg-gray-700"
                            aria-label={isMaximized ? restoreButtonLabel : maximizeButtonLabel}
                            title={isMaximized ? restoreButtonLabel : maximizeButtonLabel}
                        >
                            {isMaximized ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
                        </Button>
                    )}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 hover:bg-red-200 dark:hover:bg-red-900/20 hover:text-red-600"
                                aria-label={closeButtonLabel}
                                title={closeButtonLabel}
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{closeDialogTitle}</AlertDialogTitle>
                                <AlertDialogDescription>
                                    {closeDialogDescription}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{closeDialogCancelLabel}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => onCreateNewSession?.()}>{closeDialogActionLabel}</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col p-4">
                {children}
            </div>
        </div>
    )
}

export default Widget